import { useState } from "react";
import {
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

const SCHEMES = ["https://", "http://"] as const;

type Scheme = (typeof SCHEMES)[number];

function splitScheme(value: string): { scheme: Scheme | null; rest: string } {
  const match = SCHEMES.find((s) => value.startsWith(s));
  if (match) {
    return { scheme: match, rest: value.slice(match.length) };
  }
  return { scheme: null, rest: value };
}

export default function SchemeInput({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  size = "small",
  direction = "row",
  onEnter,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  size?: "small" | "medium";
  direction?: "row" | "column";
  onEnter?: () => void;
}) {
  const isColumn = direction === "column";
  const parsed = splitScheme(value);
  const rest = parsed.rest;

  // The selected scheme persists locally so switching it is reflected even
  // when the field is empty (an empty field emits "" so the parent's
  // required-check still works, but the dropdown must not snap back).
  const [scheme, setScheme] = useState<Scheme>(parsed.scheme ?? "https://");
  const activeScheme = parsed.scheme ?? scheme;

  const emit = (nextScheme: Scheme, nextRest: string) => {
    setScheme(nextScheme);
    onChange(nextRest ? `${nextScheme}${nextRest}` : "");
  };

  return (
    <Stack spacing={0.5} sx={{ width: "100%" }}>
      <Stack
        direction={isColumn ? "column" : "row"}
        spacing={isColumn ? 1.5 : 1}
        sx={{ width: "100%" }}
      >
        <Select
          size={size}
          value={activeScheme}
          onChange={(e) => emit(e.target.value as Scheme, rest)}
          sx={{
            minWidth: 130,
            flexShrink: 0,
            alignSelf: isColumn ? "stretch" : "auto",
            "& .MuiSelect-select": { fontFamily: "monospace" },
          }}
        >
          {SCHEMES.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontFamily: "monospace" }}>
              {s}
            </MenuItem>
          ))}
        </Select>
        <TextField
          label={label}
          fullWidth
          size={size}
          placeholder={placeholder}
          value={rest}
          onChange={(e) =>
            emit(activeScheme, e.target.value.replace(/^https?:\/\//, ""))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
        />
      </Stack>
      {helperText && (
        <FormHelperText sx={{ mx: 0, mt: 1 }}>{helperText}</FormHelperText>
      )}
    </Stack>
  );
}
